// Page views — angulartics' virtual page tracking with careero's internal handler
// (`common/analytics/angulartics-internal-analytics/angulartics.internal.analytics.module.ng1.ts`),
// which every chrome-extension screen loads through `woo.common.analytics`.
//
// WHEN, measured against angulartics 1.6.0 and ui-router 0.2.18 (careero's shrinkwrap): one
// `pageTrack` per `$stateChangeSuccess`, i.e. per transition that SETTLED — a resolve that
// redirects supersedes its transition and only the destination is tracked. No dedupe, no
// first-page track (the app has states, so `autoTrackFirstPage` stands down and the initial
// transition is the first page). Then careero's handler drops every state whose INHERITED
// `data.track` is not true — which is the auth screens, four full-page dialogs, and everything
// under the two products, `/extension/shortlist/**` and `/extension/client-contact/**`.
//
// WHAT, per `registerPageTrack` (`category: 'pageview'`, `GET /track/l`):
//   url        `basePath + $location.url()`, `basePath` being `''` — the page's own path+query+hash;
//   action     `''`, except on the two products: `ShortlistVisible` / `ShortlistHidden` from the
//              shell, `''` when it does not answer (`getPageViewActionString`);
//   value      the URL's `candidateId`, else absent — so a CONTACT profile carries none;
//   label      `data.analyticsLabel`, which no chrome-extension state declares;
//   extraInfo  `''`, except on the products: `userAgent=<UA>@@@view=<xs|sm|md|lg>`, then the
//              shortlist extra info of the page's entity (`getShortlistAnalyticsExtraInfo`).
//
// ⚠️ THE URL AND THE ACTION ARE THE CONTRACT. `WooActionTrackingDataImporter` turns pageview rows
// into BI candidate/contact views by prefix (`/extension/shortlist/candidate/profile/more`, and
// `ShortlistVisible` on the profile paths), so both are upstream's exactly, and the URL is only
// ever the SPA's own location — whose paths are upstream's ui-router URLs (CLAUDE.md constraint 1).
//
// ⚠️ INSTALLED ON THE ROUTER, NOT IN A LAYOUT EFFECT, for two reasons. Five route branches have no
// common component (see `installAuthStateListener`); and the router's state only reports a
// location once its loaders have settled, so a loader `redirect()` is tracked once, at its
// destination, exactly as a superseded ui-router transition is. StrictMode's doubled effects never
// reach it. A screen that redirects from an effect instead does produce two views — so does a
// controller's `$state.go` upstream.
import type { DataRouter, Location } from 'react-router-dom'
import { routes } from '@woo-ext/protocol'
import { trackEventWithUrl } from '@woo-ext/api'
import { EXTRA_INFO_DELIMITER, isGuarded, onGuardVerdict, readViewType, shortlistExtraInfo, trackingPageUrl } from '@woo-ext/ui'
import { inheritedData } from './routes/routeData.ts'
import { trackingContext } from './routes/actionTracking.ts'
import { pageEntity, type PageEntityKind } from './pageEntity.ts'
import { pageViewAction, shortlistSessionHostInfo } from './shortlistSession.ts'

const BASE = '/chrome-extension/extension'

/** `navigator.userAgent`, read once — upstream builds the string at module load. */
const USER_AGENT_EXTRA_INFO = `userAgent=${typeof navigator === 'undefined' ? '' : navigator.userAgent}`

/** A concrete state at this path, and `data.track` on it or an ancestor. */
export function isTrackedPage(pathname: string): boolean {
  if (!routes.some((route) => route.path === pathname && !route.abstract)) return false
  return inheritedData(pathname, 'track') === true
}

/** Which product's state hooks apply — the two states that declare `getShortlistAnalyticsExtraInfo`. */
export function pageProduct(pathname: string): PageEntityKind | undefined {
  if (pathname === `${BASE}/shortlist` || pathname.startsWith(`${BASE}/shortlist/`)) return 'candidate'
  if (pathname === `${BASE}/client-contact` || pathname.startsWith(`${BASE}/client-contact/`)) return 'clientContact'
  return undefined
}

/** `new URL(url, "https://woo.io").searchParams.get("candidateId")`, empty treated as absent. */
function candidateIdOf(url: string): string | undefined {
  try {
    const id = new URL(url, 'https://woo.io').searchParams.get('candidateId')
    return id === null || id === '' ? undefined : id
  } catch {
    return undefined
  }
}

const PROBE_WAIT_ATTEMPTS = 40
const PROBE_WAIT_MS = 25

/**
 * `bsViewDetector.getViewType()` once the probes exist.
 *
 * ⚠️ THE FIRST PAGE SETTLES BEFORE REACT HAS RENDERED ANYTHING. A branch with no loader is
 * initialised synchronously inside `createBrowserRouter`, so the first page view is taken before
 * `createRoot().render()` has committed `<BSViewIndicators>` — and read then, every first page
 * said `view=undefined` (measured) where upstream, whose probes are in the JSP's body, says `md`.
 */
async function renderedViewType(): Promise<string | undefined> {
  for (let attempt = 0; attempt < PROBE_WAIT_ATTEMPTS; attempt += 1) {
    if (document.querySelector('.device-xs') !== null) break
    await new Promise((resolve) => setTimeout(resolve, PROBE_WAIT_MS))
  }
  return readViewType()
}

/** The `pageTrack` handler for one settled location. Fire-and-forget. */
export async function trackPageView(location: Pick<Location, 'pathname' | 'search' | 'hash'>): Promise<void> {
  const url = trackingPageUrl(location)
  const product = pageProduct(location.pathname)
  // `getAnalyticsInfo`: nothing at all unless the state declares `analyticsExtraInfo`, which
  // only the two product states do — and then the bootstrap breakpoint is appended after it.
  const parts: string[] = []
  if (product !== undefined) parts.push(USER_AGENT_EXTRA_INFO, `view=${String(await renderedViewType())}`)
  let action = ''
  if (product !== undefined) {
    const entity = await pageEntity(product, location)
    const host = await shortlistSessionHostInfo()
    parts.push(shortlistExtraInfo(entity, null, { ...trackingContext(location.pathname), host: host.host, subHost: host.subHost ?? null }))
    action = await pageViewAction()
  }
  const value = candidateIdOf(url)
  await trackEventWithUrl({
    url,
    category: 'pageview',
    action,
    ...(value === undefined ? {} : { value }),
    extraInfo: parts.join(EXTRA_INFO_DELIMITER),
  })
}

/**
 * Whether a mounted `useAuthGuard` decides this location: every `/extension/**` screen
 * (`ExtensionLayout`, `requireSession`) and the auth screens that declare a rule (`AuthLayout`).
 */
export function isGuardedPage(pathname: string): boolean {
  if (pathname.startsWith(`${BASE}/`)) return true
  return pathname.startsWith('/chrome-extension/auth/') && isGuarded(pathname)
}

/** Guarded locations waiting for their verdict, by location key. Bounded — see `pageEntity.ts`. */
const awaitingVerdict = new Map<string, (allowed: boolean) => void>()
const MAX_AWAITING_VERDICT = 20

/**
 * Track every location the router settles on. Returns the unsubscribe.
 *
 * ⚠️ A GUARDED LOCATION IS TRACKED ONLY ONCE ITS GUARD ALLOWS IT. Upstream's guard rejects the
 * transition in `$stateChangeStart`, so a refused state never succeeds and is never tracked; this
 * port settles the location first and redirects from the layout's guard after. Waiting for the
 * guard's verdict on THIS location (`onGuardVerdict`) is what keeps a signed-out visit to a
 * shortlist URL to one page view, `/auth/login`, as upstream records it.
 *
 * ⚠️ A REPEAT OF THE SAME URL IS NOT A PAGE VIEW. ui-router ignores `$state.go` to the state and
 * params it is already in — no transition, no `$stateChangeSuccess` — where `navigate()` to the
 * current URL still makes a new history entry; keying on the location alone would record that.
 */
export function installPageTracking(router: DataRouter): () => void {
  let lastKey: string | undefined
  let lastUrl: string | undefined
  const stopVerdicts = onGuardVerdict((location, verdict) => {
    const settle = awaitingVerdict.get(location.key)
    if (settle === undefined) return
    awaitingVerdict.delete(location.key)
    settle(verdict.kind === 'allow')
  })
  const onState = (state: DataRouter['state']): void => {
    if (!state.initialized || state.navigation.state !== 'idle') return
    if (state.location.key === lastKey) return
    lastKey = state.location.key
    const url = trackingPageUrl(state.location)
    if (url === lastUrl) return
    lastUrl = url
    const location = state.location
    if (!isTrackedPage(location.pathname)) return
    if (!isGuardedPage(location.pathname)) {
      trackPageView(location).catch(() => undefined)
      return
    }
    awaitingVerdict.set(location.key, (allowed) => {
      if (allowed) trackPageView(location).catch(() => undefined)
    })
    // A location whose guard never answered — navigated away first — is a superseded transition.
    if (awaitingVerdict.size > MAX_AWAITING_VERDICT) {
      const oldest = awaitingVerdict.keys().next().value
      if (oldest !== undefined) awaitingVerdict.delete(oldest)
    }
  }
  onState(router.state)
  const stopRouter = router.subscribe(onState)
  return () => {
    stopRouter()
    stopVerdicts()
  }
}
